/**
 * 🎯 Utility para injetar scripts de tracking (Pixels, Analytics, etc) no checkout
 * @version 3.0.0 - Com try-catch EMBUTIDO no código JavaScript injetado
 */

/**
 * Injeta Facebook Pixel no head da página
 * @param {string} pixelCode - Código do Facebook Pixel
 */
export const injectFacebookPixel = (pixelCode) => {
  console.log('🔍 injectFacebookPixel chamado com:', { pixelCode, type: typeof pixelCode });
  
  if (!pixelCode || typeof window === 'undefined' || typeof pixelCode !== 'string' || pixelCode.trim() === '') {
    console.log('⏭️ injectFacebookPixel: código inválido, ignorando');
    return;
  }

  try {
    // Remove script anterior se existir
    const existingScript = document.querySelector('#facebook-pixel-script');
    if (existingScript) {
      existingScript.remove();
    }

    // Criar e injetar novo script com try-catch embutido para proteger a execução
    const script = document.createElement('script');
    script.id = 'facebook-pixel-script';
    script.type = 'text/javascript';
    // Envolver o código do usuário em try-catch para evitar que erros quebrem a página
    script.text = `
      try {
        ${pixelCode}
      } catch (error) {
        console.warn('⚠️ Facebook Pixel: código inválido ou com erro, ignorado silenciosamente', error);
      }
    `;
    document.head.appendChild(script);
    
    console.log('✅ Facebook Pixel injetado com proteção try-catch');
  } catch (error) {
    console.error('❌ Erro ao injetar Facebook Pixel:', error);
  }
};

/**
 * Injeta Google Analytics no head da página
 * @param {string} analyticsCode - Código do Google Analytics
 */
export const injectGoogleAnalytics = (analyticsCode) => {
  if (!analyticsCode || typeof window === 'undefined' || typeof analyticsCode !== 'string' || analyticsCode.trim() === '') return;

  try {
    // Remove script anterior se existir
    const existingScript = document.querySelector('#google-analytics-script');
    if (existingScript) {
      existingScript.remove();
    }

    // Criar e injetar novo script com try-catch embutido
    const script = document.createElement('script');
    script.id = 'google-analytics-script';
    script.type = 'text/javascript';
    script.text = `
      try {
        ${analyticsCode}
      } catch (error) {
        console.warn('⚠️ Google Analytics: código inválido ou com erro, ignorado silenciosamente', error);
      }
    `;
    document.head.appendChild(script);
    
    console.log('✅ Google Analytics injetado com proteção try-catch');
  } catch (error) {
    console.error('❌ Erro ao injetar Google Analytics:', error);
  }
};

/**
 * Injeta Google Tag Manager no head da página
 * @param {string} gtmCode - Código do Google Tag Manager
 */
export const injectGoogleTagManager = (gtmCode) => {
  if (!gtmCode || typeof window === 'undefined' || typeof gtmCode !== 'string' || gtmCode.trim() === '') return;

  try {
    // Remove script anterior se existir
    const existingScript = document.querySelector('#google-tag-manager-script');
    if (existingScript) {
      existingScript.remove();
    }

    // Criar e injetar novo script com try-catch embutido
    const script = document.createElement('script');
    script.id = 'google-tag-manager-script';
    script.type = 'text/javascript';
    script.text = `
      try {
        ${gtmCode}
      } catch (error) {
        console.warn('⚠️ Google Tag Manager: código inválido ou com erro, ignorado silenciosamente', error);
      }
    `;
    document.head.appendChild(script);
    
    console.log('✅ Google Tag Manager injetado com proteção try-catch');
  } catch (error) {
    console.error('❌ Erro ao injetar Google Tag Manager:', error);
  }
};

/**
 * Injeta código de chat (JivoChat, Tawk.to, etc) no body da página
 * @param {string} chatCode - Código do chat
 */
export const injectChatCode = (chatCode) => {
  if (!chatCode || typeof window === 'undefined' || typeof chatCode !== 'string' || chatCode.trim() === '') return;

  try {
    // Remove chat anterior se existir
    const existingChat = document.querySelector('#chat-widget-script');
    if (existingChat) {
      existingChat.remove();
    }

    // Criar e injetar novo script com try-catch embutido
    const script = document.createElement('script');
    script.id = 'chat-widget-script';
    script.type = 'text/javascript';
    script.text = `
      try {
        ${chatCode}
      } catch (error) {
        console.warn('⚠️ Chat widget: código inválido ou com erro, ignorado silenciosamente', error);
      }
    `;
    document.body.appendChild(script);
    
    console.log('✅ Chat widget injetado com proteção try-catch');
  } catch (error) {
    console.error('❌ Erro ao injetar Chat widget:', error);
  }
};

/**
 * Injeta todos os scripts de tracking de uma vez
 * @param {Object} pixelConfig - Configuração dos pixels
 * @param {string} pixelConfig.facebook_pixel - Código do Facebook Pixel
 * @param {string} pixelConfig.google_analytics - Código do Google Analytics
 * @param {string} pixelConfig.google_tag_manager - Código do Google Tag Manager
 * @param {string} chatCode - Código do chat
 */
export const injectAllTrackingScripts = (pixelConfig = {}, chatCode = '') => {
  console.log('🎯 injectAllTrackingScripts chamado com:', { pixelConfig, chatCode });
  
  // Validação extra: verificar se pixelConfig é um objeto válido
  if (!pixelConfig || typeof pixelConfig !== 'object') {
    console.log('⚠️ pixelConfig inválido, abortando');
    return;
  }

  // Facebook Pixel - se tiver código, tenta injetar
  if (pixelConfig.facebook_pixel && typeof pixelConfig.facebook_pixel === 'string' && pixelConfig.facebook_pixel.trim()) {
    injectFacebookPixel(pixelConfig.facebook_pixel);
  }

  // Google Analytics - se tiver código, tenta injetar
  if (pixelConfig.google_analytics && typeof pixelConfig.google_analytics === 'string' && pixelConfig.google_analytics.trim()) {
    injectGoogleAnalytics(pixelConfig.google_analytics);
  }

  // Google Tag Manager - se tiver código, tenta injetar
  if (pixelConfig.google_tag_manager && typeof pixelConfig.google_tag_manager === 'string' && pixelConfig.google_tag_manager.trim()) {
    injectGoogleTagManager(pixelConfig.google_tag_manager);
  }

  // Chat Code - se tiver código, tenta injetar
  if (chatCode && typeof chatCode === 'string' && chatCode.trim()) {
    injectChatCode(chatCode);
  }
};

/**
 * Remove todos os scripts de tracking
 */
export const removeAllTrackingScripts = () => {
  const scripts = [
    '#facebook-pixel-script',
    '#google-analytics-script',
    '#google-tag-manager-script',
    '#chat-widget-script'
  ];

  scripts.forEach(selector => {
    const script = document.querySelector(selector);
    if (script) {
      script.remove();
    }
  });

  console.log('🗑️ Scripts de tracking removidos');
};

/**
 * Verifica se um código de tracking é válido (contém tags de script ou funções JS)
 * @param {string} code - Código a ser verificado
 * @returns {boolean}
 */
export const isValidTrackingCode = (code) => {
  if (!code || typeof code !== 'string') return false;
  
  const trimmedCode = code.trim();
  
  // Código muito curto (< 20 chars) provavelmente não é válido
  if (trimmedCode.length < 20) {
    console.warn('⚠️ Código muito curto para ser válido:', trimmedCode);
    return false;
  }
  
  // Verifica se contém palavras-chave comuns de tracking/JavaScript
  const hasValidKeywords = 
    code.includes('<script') || 
    code.includes('gtag') || 
    code.includes('fbq') || 
    code.includes('function') ||
    code.includes('window') ||
    code.includes('document') ||
    code.includes('var ') ||
    code.includes('const ') ||
    code.includes('let ');
  
  if (!hasValidKeywords) {
    console.warn('⚠️ Código não contém palavras-chave válidas de JavaScript:', trimmedCode.substring(0, 50) + '...');
    return false;
  }
  
  return true;
};

